import React, { useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { Textarea } from 'app/components/ui/textarea';
import { Switch } from 'app/components/ui/switch';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'app/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'app/components/ui/select';
import { Separator } from 'app/components/ui/separator';
import {
  Mail,
  Send,
  Shield,
  CheckCircle,
  Save,
  AlertTriangle,
} from 'lucide-react';
import { useSettingsSlice } from '../slice';

interface SMTPSettingsProps {
  form: UseFormReturn<any>;
}

// Helper function to create payload with is_public: 0 for all keys
const createSMTPSettingsPayload = (formData: any) => {
  const payload: Record<string, { option_value: any; is_public: number }> = {};

  // Add is_public: 0 for all SMTP settings (sensitive data)
  const settingsKeys = [
    'smtp_enabled',
    'smtp_host',
    'smtp_port',
    'smtp_username',
    'smtp_password',
    'smtp_encryption',
    'smtp_from_address',
    'smtp_from_name',
    'smtp_timeout',
    'smtp_verify_peer',
    'smtp_verify_peer_name',
    'smtp_allow_self_signed',
  ];

  settingsKeys.forEach(key => {
    if (formData[key] !== undefined) {
      payload[key] = {
        option_value: formData[key],
        is_public: 0,
      };
    }
  });

  return payload;
};

export default function SMTPSettings({ form }: SMTPSettingsProps) {
  const [updateStatus, setUpdateStatus] = useState<
    'idle' | 'success' | 'error'
  >('idle');

  const { useSaveSiteSettingsMutation } = useSettingsSlice();
  const [saveSiteSettings, { isLoading: isUpdating }] =
    useSaveSiteSettingsMutation();

  // Function to handle settings update with is_public: 0 for all keys
  const handleUpdateSettings = async () => {
    try {
      setUpdateStatus('idle');
      const formData = form.getValues();
      const payload = createSMTPSettingsPayload(formData);

      console.log('SMTP settings payload with is_public: 0:', payload);

      // Use the Redux mutation to save settings
      const result = await saveSiteSettings(payload).unwrap();
      console.log('SMTP settings updated successfully:', result);

      setUpdateStatus('success');
      return result;
    } catch (error) {
      console.error('Error updating SMTP settings:', error);
      setUpdateStatus('error');
      throw error;
    }
  };

  return (
    <>
      {/* SMTP Connection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Send className="w-5 h-5" />
            SMTP Connection
          </CardTitle>
          <CardDescription>
            Configure your SMTP server connection settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="smtp_host">SMTP Host</Label>
              <Input
                id="smtp_host"
                {...form.register('smtp_host')}
                placeholder="smtp.hostinger.com"
              />
              <p className="text-xs text-muted-foreground">
                Your SMTP server hostname
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtp_port">SMTP Port</Label>
              <Input
                id="smtp_port"
                {...form.register('smtp_port')}
                placeholder="465"
                type="number"
              />
              <p className="text-xs text-muted-foreground">
                Common ports: 587 (TLS), 465 (SSL), 25 (Standard)
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtp_username">SMTP Username</Label>
              <Input
                id="smtp_username"
                {...form.register('smtp_username')}
                placeholder="support@example.com"
              />
              <p className="text-xs text-muted-foreground">
                Usually your email address
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtp_password">SMTP Password</Label>
              <Input
                id="smtp_password"
                {...form.register('smtp_password')}
                type="password"
                placeholder="your-smtp-password"
              />
              <p className="text-xs text-muted-foreground">
                Your email password or app-specific password
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Email Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Email Configuration
          </CardTitle>
          <CardDescription>
            Configure sender information and email settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="smtp_from_email">From Email</Label>
              <Input
                id="smtp_from_email"
                {...form.register('smtp_from_email')}
                placeholder="noreply@yourdomain.com"
              />
              <p className="text-xs text-muted-foreground">
                Default sender email address
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtp_from_name">From Name</Label>
              <Input
                id="smtp_from_name"
                {...form.register('smtp_from_name')}
                placeholder="Your Site Name"
              />
              <p className="text-xs text-muted-foreground">
                Display name for sender
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtp_reply_to">Reply-To Email</Label>
              <Input
                id="smtp_reply_to"
                {...form.register('smtp_reply_to')}
                placeholder="support@yourdomain.com"
              />
              <p className="text-xs text-muted-foreground">
                Email address for replies
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtp_max_retries">Max Retries</Label>
              <Input
                id="smtp_max_retries"
                {...form.register('smtp_max_retries')}
                placeholder="3"
                type="number"
              />
              <p className="text-xs text-muted-foreground">
                Maximum email sending retry attempts
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Security Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Security Settings
          </CardTitle>
          <CardDescription>
            Configure email security and encryption settings.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            <Label>Encryption Type</Label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="smtp_ssl_enabled"
                  checked={form.watch('smtp_ssl_enabled')}
                  onCheckedChange={value =>
                    form.setValue('smtp_ssl_enabled', value)
                  }
                />
                <Label htmlFor="smtp_ssl_enabled">SSL (Port 465)</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="smtp_tls_enabled"
                  checked={form.watch('smtp_tls_enabled')}
                  onCheckedChange={value =>
                    form.setValue('smtp_tls_enabled', value)
                  }
                />
                <Label htmlFor="smtp_tls_enabled">TLS (Port 587)</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="smtp_allow_insecure"
                  checked={form.watch('smtp_allow_insecure')}
                  onCheckedChange={value =>
                    form.setValue('smtp_allow_insecure', value)
                  }
                />
                <Label htmlFor="smtp_allow_insecure">Allow Insecure</Label>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <Label>Authentication</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="smtp_auth_enabled"
                  checked={form.watch('smtp_auth_enabled')}
                  onCheckedChange={value =>
                    form.setValue('smtp_auth_enabled', value)
                  }
                />
                <Label htmlFor="smtp_auth_enabled">Enable Authentication</Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="smtp_verify_peer"
                  checked={form.watch('smtp_verify_peer')}
                  onCheckedChange={value =>
                    form.setValue('smtp_verify_peer', value)
                  }
                />
                <Label htmlFor="smtp_verify_peer">Verify SSL Certificate</Label>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Testing & Monitoring */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <CheckCircle className="w-5 h-5" />
            Testing & Monitoring
          </CardTitle>
          <CardDescription>
            Test your SMTP configuration and monitor email delivery.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="smtp_test_email">Test Email Address</Label>
              <Input
                id="smtp_test_email"
                {...form.register('smtp_test_email')}
                placeholder="test@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="smtp_log_level">Log Level</Label>
              <Select
                value={form.watch('smtp_log_level')}
                onValueChange={value => form.setValue('smtp_log_level', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select log level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  <SelectItem value="error">Error Only</SelectItem>
                  <SelectItem value="warning">Warning</SelectItem>
                  <SelectItem value="info">Info</SelectItem>
                  <SelectItem value="debug">Debug</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-4">
            <Label>Email Operations</Label>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Send className="w-4 h-4 mr-2" />
                Test Connection
              </Button>
              <Button variant="outline" size="sm">
                <Mail className="w-4 h-4 mr-2" />
                Send Test Email
              </Button>
              <Button variant="outline" size="sm">
                <CheckCircle className="w-4 h-4 mr-2" />
                Check Status
              </Button>
            </div>
          </div>

          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-green-600" />
              <div>
                <p className="text-sm text-green-800 font-medium">
                  SMTP Benefits
                </p>
                <ul className="text-sm text-green-700 mt-2 space-y-1">
                  <li>• Reliable email delivery for notifications</li>
                  <li>• Password reset and account verification</li>
                  <li>• Newsletter and marketing emails</li>
                  <li>• Order confirmations and receipts</li>
                </ul>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Fixed Save Button - Bottom Right */}
      <div className="fixed bottom-14 right-6 z-50">
        <div className="flex flex-col items-end gap-3">
          {/* Status Feedback */}
          {updateStatus === 'success' && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg shadow-lg max-w-sm">
              <div className="flex items-center gap-2 text-green-800">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  SMTP settings saved successfully!
                </span>
              </div>
            </div>
          )}

          {updateStatus === 'error' && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg shadow-lg max-w-sm">
              <div className="flex items-center gap-2 text-red-800">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">
                  Failed to save SMTP settings. Please try again.
                </span>
              </div>
            </div>
          )}

          {/* Save Button */}
          <Button
            onClick={handleUpdateSettings}
            className="shadow-lg hover:shadow-xl transition-all duration-200"
            variant="default"
            disabled={isUpdating}
            size="lg"
          >
            {isUpdating ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save
              </>
            )}
          </Button>
        </div>
      </div>
    </>
  );
}
